/**
 * 文生图节点 - 基于纯文本提示词生成图像（即梦4.0）
 */
import React, { memo, useState, useEffect } from 'react';
import { useReactFlow } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { NodeLayout } from './NodeLayout';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import ApiService from '../../../api/ApiService';
import LocalDataService from '../../../api/LocalDataService';
import { toast } from 'react-toastify';
import './TextToImageNode.css';

const apiService = new ApiService();

export const TextToImageNode = memo(({ id, data }) => {
  const { updateNodeData, getNode, getEdges } = useReactFlow();
  const { addNodesWithEdges } = useNodeOperations();
  const { readOnly } = useProject();
  
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || 'landscape_16_9');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    updateNodeData(id, {
      aspectRatio,
    });
  }, [id, aspectRatio, updateNodeData]);

  // 获取连接到此节点的所有文本输入节点
  const getConnectedPromptNodes = () => {
    const edges = getEdges();
    const connectedEdges = edges.filter(edge => edge.target === id);
    
    const prompts = [];
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      if (sourceNode && sourceNode.type === 'promptInput' && sourceNode.data?.prompt) {
        prompts.push(sourceNode.data.prompt);
      }
    });
    
    return prompts;
  };

  const handleGenerate = async () => {
    try {
      setError(null);
      setIsGenerating(true);
      
      // 1. 获取连接的提示词
      const prompts = getConnectedPromptNodes();
      if (prompts.length === 0) {
        setError('请先连接文本输入节点');
        setIsGenerating(false);
        return;
      }
      
      const prompt = prompts.join('\n'); // 合并多个提示词
      console.log('使用提示词:', prompt);
      
      // 2. 获取用户token
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        setError('请先登录');
        toast.error('请先登录');
        setIsGenerating(false);
        return;
      }
      
      // 3. 调用即梦4.0 API创建任务
      const inputParams = {
        prompt: prompt,
        aspect_ratio: aspectRatio,
      };
      
      const response = await apiService.request(
        `${apiService.apiBase}/fal/tasks/create`,
        'POST',
        {
          token: userData.token,
          user_type: 1,
          app_name: 'fal-ai/bytedance/seedream/v4/text-to-image',
          input_params: inputParams
        }
      );
      
      console.log('任务创建响应:', response);
      
      if (response.code === 200 && response.data) {
        const newTaskId = response.data.task_id;
        toast.success('任务已创建！');
        
        // 4. 获取当前节点位置，创建任务展示节点在右侧
        const currentNode = getNode(id);
        const newNodePosition = {
          x: currentNode.position.x + 400, // 在右侧400px处
          y: currentNode.position.y,
        };
        
        // 5. 创建 ImageTaskNode 节点
        const taskNodeId = nanoid();
        const taskNode = {
          id: taskNodeId,
          type: 'imageTask',
          position: newNodePosition,
          data: {
            taskId: newTaskId,
            taskType: 'text-to-image',
            status: 'pending',
            inputParams: inputParams,
            generatedImages: [],
          },
        };
        
        // 6. 使用统一方法创建节点和连接边
        const newEdge = {
          id: nanoid(),
          source: id, // 当前文生图节点
          target: taskNodeId, // 新创建的任务节点
          type: 'default',
        };
        
        // 使用统一的添加节点+连线方法
        await addNodesWithEdges([taskNode], [newEdge]);
        console.log('已创建任务节点和连接线:', taskNodeId);
        
        setIsGenerating(false);
      } else {
        throw new Error(response.message || '创建任务失败');
      }
      
    } catch (err) {
      console.error('文生图失败:', err);
      const errorMessage = err?.data?.detail || err?.message || '生成失败，请重试';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsGenerating(false);
    }
  };

  return (
    <NodeLayout id={id} type="textToImage" title="文生图（即梦4.0）">
      <div className="TextToImageNode-wrapper">
        {/* 宽高比 */}
        <div className="TextToImageNode-field">
          <label className="TextToImageNode-label">宽高比</label>
          <select
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
            disabled={isGenerating || readOnly}
            className="TextToImageNode-select"
          >
            <option value="square_hd">1:1 正方形</option>
            <option value="landscape_16_9">16:9 横屏</option>
            <option value="portrait_16_9">9:16 竖屏</option>
            <option value="portrait_4_3">4:3 传统</option>
            <option value="landscape_4_3">3:4 竖版</option>
          </select>
        </div>

        {/* 提示 */}
        <div className="TextToImageNode-hint">
          💡 点击生成后会自动创建任务节点<br />
          任务节点会实时显示生成状态和结果
        </div>

        {/* 生成按钮 */}
        <button
          onClick={handleGenerate}
          disabled={isGenerating || readOnly}
          className={`TextToImageNode-generate ${isGenerating || readOnly ? 'TextToImageNode-generate-disabled' : ''}`}
        >
          {isGenerating ? '创建中...' : '创建任务'}
        </button>

        {/* 错误提示 */}
        {error && (
          <div className="TextToImageNode-error">
            {error}
          </div>
        )}
      </div>
    </NodeLayout>
  );
});

TextToImageNode.displayName = 'TextToImageNode';

